import { makeAutoObservable, runInAction } from "mobx";

import type {
  ArchiveRecordsUseCase,
  AssignRecordToProjectUseCase,
  ChangeRecordStatusUseCase,
  DeleteRecordsUseCase,
  RenameRecordUseCase,
  RemoveFromProjectUseCase,
} from "@/application/records/use-cases";
import type { ToastServicePort } from "@/application/records/ports/ToastServicePort";
import { BaseOptimisticMutation } from "@/application/common/base/BaseOptimisticMutation";
import { RecordsListStore } from "./RecordsListStore";
import { RecordsMetaStore } from "./RecordsMetaStore";
import { RecordsStateManager } from "./RecordsStateManager";

export interface RecordsMutationsStoreDependencies {
  deleteRecordsUseCase: DeleteRecordsUseCase;
  archiveRecordsUseCase: ArchiveRecordsUseCase;
  renameRecordUseCase: RenameRecordUseCase;
  removeFromProjectUseCase: RemoveFromProjectUseCase;
  assignRecordToProjectUseCase: AssignRecordToProjectUseCase;
  changeRecordStatusUseCase: ChangeRecordStatusUseCase;
  toastService: ToastServicePort;
  userListStore: RecordsListStore;
  adminListStore: RecordsListStore;
  metaStore: RecordsMetaStore;
  stateManager?: RecordsStateManager;
}

export class RecordsMutationsStore {
  private readonly deleteRecordsUseCase: DeleteRecordsUseCase;
  private readonly archiveRecordsUseCase: ArchiveRecordsUseCase;
  private readonly renameRecordUseCase: RenameRecordUseCase;
  private readonly removeFromProjectUseCase: RemoveFromProjectUseCase;
  private readonly assignRecordToProjectUseCase: AssignRecordToProjectUseCase;
  private readonly changeRecordStatusUseCase: ChangeRecordStatusUseCase;
  private readonly toastService: ToastServicePort;
  private readonly userListStore: RecordsListStore;
  private readonly adminListStore: RecordsListStore;
  private readonly metaStore: RecordsMetaStore;
  private readonly stateManager?: RecordsStateManager;
  private readonly optimisticMutation: BaseOptimisticMutation;

  isDeleting = false;
  isArchiving = false;
  isRenaming = false;
  isRemovingFromProject = false;
  isAssigningToProject = false;
  isChangingStatus = false;
  deleteError: string | null = null;
  archiveError: string | null = null;
  renameError: string | null = null;
  removeFromProjectError: string | null = null;
  assignToProjectError: string | null = null;
  changeStatusError: string | null = null;

  constructor(deps: RecordsMutationsStoreDependencies) {
    this.deleteRecordsUseCase = deps.deleteRecordsUseCase;
    this.archiveRecordsUseCase = deps.archiveRecordsUseCase;
    this.renameRecordUseCase = deps.renameRecordUseCase;
    this.removeFromProjectUseCase = deps.removeFromProjectUseCase;
    this.assignRecordToProjectUseCase = deps.assignRecordToProjectUseCase;
    this.changeRecordStatusUseCase = deps.changeRecordStatusUseCase;
    this.toastService = deps.toastService;
    this.userListStore = deps.userListStore;
    this.adminListStore = deps.adminListStore;
    this.metaStore = deps.metaStore;
    this.stateManager = deps.stateManager;
    this.optimisticMutation = new BaseOptimisticMutation();

    makeAutoObservable(this, {}, { autoBind: true });
  }

  async deleteRecords(recordIds: string[]): Promise<void> {
    if (!recordIds.length) {
      return;
    }

    const userSnapshot = this.userListStore.createSnapshot();
    const adminSnapshot = this.adminListStore.createSnapshot();
    const stateSnapshot = this.stateManager?.createSnapshot();

    runInAction(() => {
      this.isDeleting = true;
      this.deleteError = null;
    });

    if (this.stateManager) {
      this.stateManager.removeRecords(recordIds);
    } else {
      this.userListStore.removeRecords(recordIds);
      this.adminListStore.removeRecords(recordIds);
    }

    this.metaStore.removeFromCache(recordIds);

    try {
      await this.deleteRecordsUseCase.execute({ recordIds });
      this.toastService.success(
        `Successfully deleted ${recordIds.length} record${
          recordIds.length > 1 ? "s" : ""
        }`
      );
    } catch (error) {
      this.userListStore.restoreSnapshot(userSnapshot);
      this.adminListStore.restoreSnapshot(adminSnapshot);
      if (stateSnapshot) {
        this.stateManager?.restoreSnapshot(stateSnapshot);
      }
      const message =
        error instanceof Error ? error.message : "Failed to delete records";
      runInAction(() => (this.deleteError = message));
      this.toastService.error(
        `Failed to delete ${recordIds.length} record${
          recordIds.length > 1 ? "s" : ""
        }`
      );
      throw error;
    } finally {
      runInAction(() => {
        this.isDeleting = false;
      });
    }
  }

  async archiveRecords(
    recordIds: string[],
    isUnarchive: boolean = false
  ): Promise<void> {
    if (!recordIds.length) {
      return;
    }

    const userSnapshot = this.userListStore.createSnapshot();
    const adminSnapshot = this.adminListStore.createSnapshot();
    const stateSnapshot = this.stateManager?.createSnapshot();

    runInAction(() => {
      this.isArchiving = true;
      this.archiveError = null;
    });

    if (this.stateManager) {
      for (const recordId of recordIds) {
        const record = this.stateManager.getRecord(recordId);
        if (!record) {
          continue;
        }
        this.stateManager.updateRecord(recordId, {
          is_archived: !record.is_archived,
        });
      }
    } else {
      this.userListStore.toggleArchive(recordIds);
      this.adminListStore.toggleArchive(recordIds);
    }

    try {
      await this.archiveRecordsUseCase.execute({ recordIds });
      this.toastService.success(
        `${recordIds.length} record${
          recordIds.length > 1 ? "s" : ""
        } successfully ${isUnarchive ? "unarchived" : "archived"}`
      );
    } catch (error) {
      this.userListStore.restoreSnapshot(userSnapshot);
      this.adminListStore.restoreSnapshot(adminSnapshot);
      if (stateSnapshot) {
        this.stateManager?.restoreSnapshot(stateSnapshot);
      }
      const message =
        error instanceof Error
          ? error.message
          : "Failed to update archive status";
      runInAction(() => (this.archiveError = message));
      this.toastService.error(
        `Failed to update archive status for ${recordIds.length} record${
          recordIds.length > 1 ? "s" : ""
        }`
      );
      throw error;
    } finally {
      runInAction(() => {
        this.isArchiving = false;
      });
    }
  }

  async renameRecord(recordId: string, newName: string): Promise<void> {
    if (!recordId || !newName.trim()) {
      return;
    }

    const userSnapshot = this.userListStore.createSnapshot();
    const adminSnapshot = this.adminListStore.createSnapshot();
    const stateSnapshot = this.stateManager?.createSnapshot();

    runInAction(() => {
      this.isRenaming = true;
      this.renameError = null;
    });

    if (this.stateManager) {
      this.stateManager.updateRecord(recordId, { name: newName });
    } else {
      this.userListStore.renameRecord(recordId, newName);
      this.adminListStore.renameRecord(recordId, newName);
    }

    try {
      await this.renameRecordUseCase.execute({ recordId, newName });
      this.toastService.success("Record renamed successfully");
    } catch (error) {
      this.userListStore.restoreSnapshot(userSnapshot);
      this.adminListStore.restoreSnapshot(adminSnapshot);
      if (stateSnapshot) {
        this.stateManager?.restoreSnapshot(stateSnapshot);
      }
      const message =
        error instanceof Error ? error.message : "Failed to rename record";
      runInAction(() => (this.renameError = message));
      this.toastService.error("Failed to rename record. Please try again.");
      throw error;
    } finally {
      runInAction(() => {
        this.isRenaming = false;
      });
    }
  }

  async removeFromProject(
    recordIds: string[],
    projectId?: string
  ): Promise<void> {
    if (!recordIds.length) {
      return;
    }

    const userSnapshot = this.userListStore.createSnapshot();
    const adminSnapshot = this.adminListStore.createSnapshot();
    const stateSnapshot = this.stateManager?.createSnapshot();

    await this.optimisticMutation
      .executeWithOptimisticUpdate({
        updateFn: () => {
          runInAction(() => {
            this.isRemovingFromProject = true;
            this.removeFromProjectError = null;
          });
          if (this.stateManager) {
            this.stateManager.updateRecordsProject(recordIds, "", projectId);
          } else {
            for (const recordId of recordIds) {
              this.userListStore.updateRecordById(recordId, { project: "" });
              this.adminListStore.updateRecordById(recordId, { project: "" });
            }
          }
        },
        apiCall: async () => {
          await this.removeFromProjectUseCase.execute({
            recordIds,
            projectId,
          });
        },
        rollbackFn: () => {
          this.userListStore.restoreSnapshot(userSnapshot);
          this.adminListStore.restoreSnapshot(adminSnapshot);
          if (stateSnapshot) {
            this.stateManager?.restoreSnapshot(stateSnapshot);
          }
          runInAction(() => {
            this.removeFromProjectError = "Failed to remove from project";
          });
        },
        onSuccess: () => {
          this.toastService.success(
            `${recordIds.length} record${
              recordIds.length > 1 ? "s" : ""
            } removed from project`
          );
          // Инвалидировать данные для перезагрузки с сервера
          this.userListStore.invalidateAll();
          this.adminListStore.invalidateAll();
        },
        onError: (error) => {
          this.toastService.error(error.message);
        },
      })
      .finally(() => {
        runInAction(() => {
          this.isRemovingFromProject = false;
        });
      });
  }

  async assignToProject(
    recordIds: string[],
    projectId: string,
    projectName: string
  ): Promise<void> {
    if (!recordIds.length || !projectId) {
      return;
    }

    const userSnapshot = this.userListStore.createSnapshot();
    const adminSnapshot = this.adminListStore.createSnapshot();
    const stateSnapshot = this.stateManager?.createSnapshot();

    await this.optimisticMutation
      .executeWithOptimisticUpdate({
        updateFn: () => {
          runInAction(() => {
            this.isAssigningToProject = true;
            this.assignToProjectError = null;
          });
          if (this.stateManager) {
            this.stateManager.updateRecordsProject(
              recordIds,
              projectName,
              projectId
            );
          } else {
            for (const recordId of recordIds) {
              this.userListStore.updateRecordById(recordId, {
                project: projectName,
              });
              this.adminListStore.updateRecordById(recordId, {
                project: projectName,
              });
            }
          }
        },
        apiCall: async () => {
          await this.assignRecordToProjectUseCase.execute({
            recordIds,
            projectId,
          });
        },
        rollbackFn: () => {
          this.userListStore.restoreSnapshot(userSnapshot);
          this.adminListStore.restoreSnapshot(adminSnapshot);
          if (stateSnapshot) {
            this.stateManager?.restoreSnapshot(stateSnapshot);
          }
          runInAction(() => {
            this.assignToProjectError = "Failed to assign to project";
          });
        },
        onSuccess: () => {
          this.toastService.success(
            `${recordIds.length} record${
              recordIds.length > 1 ? "s" : ""
            } assigned to project`
          );
          // Инвалидировать данные для перезагрузки с сервера
          this.userListStore.invalidateAll();
          this.adminListStore.invalidateAll();
        },
        onError: (error) => {
          this.toastService.error(error.message);
        },
      })
      .finally(() => {
        runInAction(() => {
          this.isAssigningToProject = false;
        });
      });
  }

  async changeRecordStatus(
    recordId: string,
    status: string
  ): Promise<void> {
    if (!recordId || !status) {
      return;
    }

    const userSnapshot = this.userListStore.createSnapshot();
    const adminSnapshot = this.adminListStore.createSnapshot();
    const stateSnapshot = this.stateManager?.createSnapshot();

    await this.optimisticMutation
      .executeWithOptimisticUpdate({
        updateFn: () => {
          runInAction(() => {
            this.isChangingStatus = true;
            this.changeStatusError = null;
          });
          if (this.stateManager) {
            this.stateManager.updateRecord(recordId, { status });
          } else {
            this.userListStore.updateRecordById(recordId, { status });
            this.adminListStore.updateRecordById(recordId, { status });
          }
        },
        apiCall: async () => {
          await this.changeRecordStatusUseCase.execute({
            recordId,
            status,
          });
        },
        rollbackFn: () => {
          this.userListStore.restoreSnapshot(userSnapshot);
          this.adminListStore.restoreSnapshot(adminSnapshot);
          if (stateSnapshot) {
            this.stateManager?.restoreSnapshot(stateSnapshot);
          }
          runInAction(() => {
            this.changeStatusError = "Failed to change record status";
          });
        },
        onSuccess: () => {
          this.toastService.success("Record status updated successfully");
          // Инвалидировать данные для перезагрузки с сервера
          this.userListStore.invalidateAll();
          this.adminListStore.invalidateAll();
        },
        onError: (error) => {
          this.toastService.error(error.message);
        },
      })
      .finally(() => {
        runInAction(() => {
          this.isChangingStatus = false;
        });
      });
  }
}
